import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { type BTQueryOptions } from "helpers/AppProvider.api.handler";

import { BTLocalStorage } from "types/btStorage";

import { APIHandler } from "utilities/apiHandler";

import {
    getHeaderInfoFromResponse,
    type IHeaderInfoRequest,
    type IHeaderInfoResponse,
} from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo.api.types";
import {
    HasUnreadResponse,
    MainNavigationResponse,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.types";

export interface IMainNavigationHandler {
    get: () => Promise<MainNavigationResponse>;
    getHeader: (request: IHeaderInfoRequest) => Promise<IHeaderInfoResponse>;
    getHasUnreadChat: () => Promise<HasUnreadResponse>;
}

export class MainNavigationHandler implements IMainNavigationHandler {
    async get(): Promise<MainNavigationResponse> {
        return await APIHandler(`/api/menu/`, {
            method: "GET",
            responseType: MainNavigationResponse,
        });
    }

    async getHeader(request: IHeaderInfoRequest): Promise<IHeaderInfoResponse> {
        return await APIHandler(`/api/menu/HeaderInfo`, {
            method: "GET",
            data: request,
            responseType: getHeaderInfoFromResponse,
        });
    }

    async getHasUnreadChat(): Promise<HasUnreadResponse> {
        return await APIHandler(`/api/channels/read`, {
            method: "GET",
            responseType: HasUnreadResponse,
        });
    }
}

export const ChatReadQueryKey = "chatRead";
export const useGetChatQuery = (
    handler: IMainNavigationHandler,
    readPollingRateInSeconds: number,
    options?: BTQueryOptions<HasUnreadResponse, Error, string[]>,
) => {
    return useQuery(
        [ChatReadQueryKey],
        async () => {
            const response = await handler.getHasUnreadChat();
            BTLocalStorage.set("bt-boolean-hasUnreadChat", response.hasUnread);
            return response;
        },
        {
            retry: 3,
            cacheTime: moment.duration(24, "hours").asMilliseconds(),
            refetchInterval: moment.duration(readPollingRateInSeconds, "seconds").asMilliseconds(),
            staleTime: moment.duration(readPollingRateInSeconds, "seconds").asMilliseconds(),
            initialData: {
                hasUnread: BTLocalStorage.get("bt-boolean-hasUnreadChat") ?? false,
                lastCheckedDateUtc: moment.utc(),
            },
            ...options,
        },
    );
};
